import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  defaultReservationForm,
  reservationPayload,
  reservationToForm,
  toLocalDateTime,
  validateReservation,
} from '../../features/restaurant/reservations/reservationUtils';
import RestaurantSettingsService from '../../features/restaurant/settings/RestaurantSettingsService';
import { RestaurantReservationSettings } from '../../features/restaurant/settings/RestaurantSettingsTypes';
import CustomerService from '../../services/CustomerService';
import RestaurantOrderService from '../../services/restaurant/RestaurantOrderService';
import RestaurantReservationService from '../../services/restaurant/RestaurantReservationService';
import RestaurantTableService from '../../services/restaurant/RestaurantTableService';
import { RootState } from '../../store/store';
import { RestaurantPartyOption } from '../../types/restaurant/RestaurantOrderTypes';
import {
  Reservation,
  ReservationAvailability,
  ReservationFieldErrors,
  ReservationFormState,
  ReservationPage,
  ReservationPageFilters,
} from '../../types/restaurant/RestaurantReservationTypes';
import { RestaurantTableDTO } from '../../types/restaurant/RestaurantTableTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const defaultSettings: RestaurantReservationSettings = {
  id: 0,
  active: false,
  createdAt: '',
  updatedAt: '',
  reservationsEnabled: false,
  reservationToleranceMinutes: 0,
  maximumAdvanceDays: 365,
  automaticCancellationEnabled: false,
  allowGuestReservations: true,
  allowMultipleTables: false,
  allowOverbooking: false,
  sendReservationReminders: false,
};

const arrayFrom = (value: any): any[] => {
  const data = value?.data ?? value;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const customerOptions = (value: any): RestaurantPartyOption[] =>
  arrayFrom(value)
    .map((item) => ({
      id: Number(item.id),
      name:
        item.name ||
        item.fullName ||
        item.tradeName ||
        `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() ||
        `#${item.id}`,
    }))
    .filter((item) => item.id);

const errorMessage = (error: any) =>
  error?.response?.data?.message || 'No se pudo completar la operación.';

const backendErrors = (error: any): ReservationFieldErrors => {
  const raw = error?.response?.data?.data;
  if (!raw || Array.isArray(raw) || typeof raw !== 'object') return {};
  const aliases: Record<string, keyof ReservationFormState> = {
    startsAt: 'date',
    durationMinutes: 'durationMinutes',
    partySize: 'partySize',
    tableIds: 'tableIds',
    customerId: 'customerId',
    guestName: 'guestName',
    guestPhone: 'guestPhone',
    guestEmail: 'guestEmail',
    notes: 'notes',
  };
  return Object.entries(raw).reduce((result, [field, message]) => {
    const key = aliases[field] || (field as keyof ReservationFormState);
    result[key] = String(message);
    return result;
  }, {} as ReservationFieldErrors);
};

const useRestaurantReservations = (branchId: number | '') => {
  const navigate = useNavigate();
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const waiterId = useSelector(
    (state: RootState) => state.userProfile.userProfile.id
  );
  const [list, setList] = useState<Reservation[]>([]);
  const [page, setPage] = useState<ReservationPage | null>(null);
  const [tables, setTables] = useState<RestaurantTableDTO[]>([]);
  const [customers, setCustomers] = useState<RestaurantPartyOption[]>([]);
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Reservation | null>(null);
  const [form, setForm] = useState(defaultReservationForm);
  const [errors, setErrors] = useState<ReservationFieldErrors>({});
  const [availability, setAvailability] = useState<ReservationAvailability[]>(
    []
  );
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestId = useRef(0);
  const contextRequestId = useRef(0);

  const load = async (filters: ReservationPageFilters, silent = false) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    silent ? setUpdating(true) : setLoading(true);
    setError('');
    try {
      const response = await RestaurantReservationService.listFiltered(
        branchId,
        filters
      );
      if (currentRequest !== requestId.current) return;
      setPage(response);
      setList(response.content ?? []);
      return response;
    } catch (cause) {
      if (currentRequest !== requestId.current) return;
      setError(errorMessage(cause));
    } finally {
      if (currentRequest === requestId.current) {
        silent ? setUpdating(false) : setLoading(false);
      }
    }
  };

  const loadContext = useCallback(async () => {
    if (!branchId) return;
    const currentRequest = ++contextRequestId.current;
    const [settingsResult, tablesResult, customersResult] =
      await Promise.allSettled([
        RestaurantSettingsService.getByBranch(branchId),
        RestaurantTableService.list(branchId, true),
        CustomerService.getDropdown(),
      ]);
    if (currentRequest !== contextRequestId.current) return;
    if (settingsResult.status === 'fulfilled') {
      setSettings(settingsResult.value.data.reservations);
    } else {
      handle(settingsResult.reason);
    }
    setTables(
      tablesResult.status === 'fulfilled' ? tablesResult.value.data ?? [] : []
    );
    setCustomers(
      customersResult.status === 'fulfilled'
        ? customerOptions(customersResult.value)
        : []
    );
  }, [branchId]);

  useEffect(() => {
    if (
      !modalOpen ||
      !branchId ||
      !form.date ||
      !form.time ||
      form.durationMinutes < 15
    )
      return;
    const timer = window.setTimeout(async () => {
      setAvailabilityLoading(true);
      try {
        const response = await RestaurantReservationService.availability(
          branchId,
          toLocalDateTime(form.date, form.time),
          form.durationMinutes,
          form.partySize || undefined
        );
        setAvailability((current) => {
          const assigned = current.filter((table) =>
            form.tableIds.includes(table.tableId)
          );
          return [
            ...response.data,
            ...assigned.filter(
              (old) =>
                !response.data.some((item) => item.tableId === old.tableId)
            ),
          ];
        });
      } catch (cause) {
        handle(cause);
        setAvailability([]);
      } finally {
        setAvailabilityLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    branchId,
    modalOpen,
    form.date,
    form.time,
    form.durationMinutes,
    form.partySize,
  ]);

  const selectedCapacity = useMemo(
    () =>
      availability
        .filter((table) => form.tableIds.includes(table.tableId))
        .reduce((sum, table) => sum + table.capacity, 0),
    [availability, form.tableIds]
  );

  const updateField = <K extends keyof ReservationFormState>(
    field: K,
    value: ReservationFormState[K]
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const openCreate = () => {
    const next = defaultReservationForm();
    if (!settings.allowGuestReservations) next.customerMode = 'REGISTERED';
    setForm(next);
    setErrors({});
    setAvailability([]);
    setModalOpen(true);
  };

  const edit = async (item: Reservation) => {
    try {
      const response = await RestaurantReservationService.get(item.id);
      const reservation = response.data;
      setForm(reservationToForm(reservation));
      setAvailability(
        reservation.tables.map((table) => ({
          tableId: table.id,
          code: table.code,
          name: table.name,
          capacity: table.capacity,
          diningRoomId: table.diningRoomId,
          diningRoomName: table.diningRoomName,
        }))
      );
      setErrors({});
      setModalOpen(true);
    } catch (cause) {
      handle(cause);
    }
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const reloadAfterMutation = async (
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) => {
    const result = await load(filters, true);
    if (result?.empty && filters.page > 0) onEmptyPage();
  };

  const save = async (
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) => {
    if (!branchId || saving) return;
    const validation = validateReservation(
      form,
      selectedCapacity,
      settings.allowOverbooking
    );
    setErrors(validation);
    if (Object.keys(validation).length) return;
    setSaving(true);
    try {
      const response = form.id
        ? await RestaurantReservationService.update(
            form.id,
            reservationPayload(form)
          )
        : await RestaurantReservationService.create(
            branchId,
            reservationPayload(form)
          );
      show(response.type, response.title, response.message);
      setModalOpen(false);
      await reloadAfterMutation(filters, onEmptyPage);
    } catch (cause) {
      setErrors(backendErrors(cause));
      handle(cause);
    } finally {
      setSaving(false);
    }
  };

  const mutate = async (
    operation: () => Promise<any>,
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) => {
    if (saving) return;
    setSaving(true);
    try {
      const response = await operation();
      show(response.type, response.title, response.message);
      await reloadAfterMutation(filters, onEmptyPage);
    } catch (cause) {
      handle(cause);
    } finally {
      setSaving(false);
    }
  };

  const confirmReservation = (
    item: Reservation,
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) =>
    mutate(
      () => RestaurantReservationService.confirm(item.id),
      filters,
      onEmptyPage
    );

  const cancelReservation = (
    reason: string,
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) => {
    if (!cancelTarget) return;
    mutate(
      () => RestaurantReservationService.cancel(cancelTarget.id, reason),
      filters,
      onEmptyPage
    ).finally(() => setCancelTarget(null));
  };

  const noShow = (
    item: Reservation,
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) =>
    confirm({
      title: 'Marcar ausencia',
      message: `¿Confirma que ${
        item.customerName || item.guestName || 'el cliente'
      } no se presentó?`,
      icon: 'warning',
      confirmText: 'Sí, marcar no-show',
      cancelText: 'Volver',
      onConfirm: () =>
        mutate(
          () => RestaurantReservationService.noShow(item.id),
          filters,
          onEmptyPage
        ),
    });

  const openOrder = async (
    item: Reservation,
    filters: ReservationPageFilters,
    onEmptyPage: () => void
  ) => {
    if (item.orderId) {
      navigate(
        `/restaurant/orders?branchId=${item.branchId}&orderId=${item.orderId}`
      );
      return;
    }
    const tableId = item.tables[0]?.id;
    if (!tableId) return;
    setSaving(true);
    try {
      const response = await RestaurantOrderService.create(item.branchId, {
        tableId,
        serviceType: 'DINE_IN',
        reservationId: item.id,
        waiterId: waiterId || null,
        notes: `Orden originada desde reservación ${item.reservationNumber}`,
      });
      show(
        response.type ?? 'success',
        response.title ?? 'Orden abierta',
        response.message ?? 'La mesa fue recibida.'
      );
      await reloadAfterMutation(filters, onEmptyPage);
      navigate(
        `/restaurant/orders?branchId=${item.branchId}&orderId=${response.data.id}`
      );
    } catch (cause) {
      handle(cause);
    } finally {
      setSaving(false);
    }
  };

  const resetResults = () => {
    requestId.current += 1;
    contextRequestId.current += 1;
    setList([]);
    setPage(null);
    setTables([]);
    setCustomers([]);
    setSettings(defaultSettings);
    setLoading(false);
    setUpdating(false);
    setError('');
  };

  return {
    list,
    page,
    tables,
    customers,
    settings,
    loading,
    updating,
    error,
    load,
    loadContext,
    resetResults,
    modalOpen,
    form,
    errors,
    availability,
    availabilityLoading,
    selectedCapacity,
    saving,
    cancelTarget,
    setCancelTarget,
    openCreate,
    edit,
    closeModal,
    updateField,
    save,
    confirmReservation,
    cancelReservation,
    noShow,
    openOrder,
  };
};

export default useRestaurantReservations;
